/**
 * Die Seite meldet dem Native, dass sie Ton spielt. (02.10.2026)
 *
 * Der Mikrofon-Motor der App (PawbotMikroMotor) darf nach einer Unterbrechung
 * nicht neben dem Ton der Seite neu anlaufen — ein GELUNGENER Start dort ließ
 * die Antwort stocken (30.09., `tts-gap via=WACHHUND`). Das Native wusste aber
 * nicht, wann die Seite spielt: `Tonbelegung.wiedergabeLaeuft` hängt an
 * `setPlaybackHiFi`, und die Seite schaltet das aus, sobald die Sprechtaste
 * beginnt (`setSeitenAufnahme`), mitten in einer Vorlesung; der native
 * Vorlese-Halt ist seit dem 24.09. abgeschaltet (`nativerVorleserAn`). Journal
 * 01.10. 21:57:43–21:58:20: `wiedergabeLaeuft:false`, und `vorlesen-stueck`
 * lief weiter.
 *
 * ## Wie
 *
 * Eine MIETE je Quelle, kurz und erneuert, nie ein Schalter:
 *  - TTS-Warteschlange (Antwort, Vorlesen — alle Anbieter laufen über ihr
 *    <audio>): `tts-audio-start` pro Stück mit Frist = Stückdauer + 3 s
 *    (unbekannte Dauer: 20 s); zurück bei `tts-audio-end` und
 *    `tts-audio-abbruch`. Bis dahin erneuert ein Takt alle 5 s (ein Stück ist
 *    länger als gedacht) — aber höchstens 5 Minuten nach dem letzten Stückbeginn
 *    oder dem Fortsetzen (`ttsWeiter`). Gezählt wird JE WARTESCHLANGE
 *    (`detail.queueId`): das Ende der einen nimmt die Miete der anderen nicht.
 *  - Elemente (`beobachteElement`; `hookHiFiPlayback` ruft es für Musik, Spiegel
 *    anhören, tiefe Fragen, Alltag und Meditation): von `play` bis
 *    `pause`/`ended`/`error`; der Takt prüft den echten Zustand des Elements,
 *    weil keine Ereignisliste vollständig ist.
 * Bei Funkstille (WebView eingefroren, Brücke weg) läuft die Miete im Native
 * von selbst ab (`MikroMotorRegel.SeitenTonMiete`, höchstens 120 s). Die
 * Erneuerung trägt 30 s: WebKit drosselt Timer im Hintergrund.
 *
 * ## Rücknahme mit Nachlauf (Opus 3, 02.10.)
 *
 * Beim Ende wird NICHT sofort zurückgenommen: das Native stößt bei der Rücknahme
 * sofort einen Start des Motors an, und zwischen zwei Abschnitten einer
 * Vorlesung, beim Wechsel zur Sprechtaste (getUserMedia beginnt erst) und beim
 * selbstöffnenden Mikro nach einer Frage liegt das neben neuem Ton. Darum läuft
 * die Miete `NACHLAUF_MS` weiter (`an:true, frist:4`), und erst nach 3 s Stille
 * kommt `an:false`; beginnt inzwischen etwas Neues, entfällt es.
 *
 * ## Für andere Wiedergabewege (z. B. ein PCM-Strom)
 *
 *  - Beginn:  `seitenTonMelder.ttsStart(dauerS?, queueId?)`  (oder das Ereignis
 *             `voice-v8:tts-audio-start` mit `detail: { dauerS, queueId }`)
 *  - Erneuern: der Takt macht es von selbst; nach einer langen Pause
 *             `seitenTonMelder.ttsWeiter(queueId?)`
 *  - Ende:    `seitenTonMelder.ttsEnde(queueId?)`  (oder `voice-v8:tts-audio-end`
 *             / `-abbruch`, ebenfalls mit `detail.queueId`; ohne Kennung nimmt
 *             das Ende ALLE zurück)
 *  - Element: `beobachteElement(audioElement)` — hängt play/pause/ended/error an
 *  - Neue Kennung: `naechsteSeitenTonId()`
 *
 * ⚠️ Ältere App-Fassungen kennen die Methode `seitenTon` nicht: dann ein
 *    stilles Nichts — kein Fehler, kein Protokoll (`nativesPluginKennt`).
 * ⚠️ NICHT gemeldet: der Browser-Anruf (webrtc.ts; in der App läuft der Anruf
 *    nativ, PawbotCall), kurze Vorhörproben (Stimmwahl in app.ts, Einstellungen,
 *    Onboarding) und kurze Töne über den AudioContext. Der Player der
 *    Projektdateien (project-file-viewer.ts) ist angeschlossen. Die Player von
 *    Meditation und Alltag melden über `hookHiFiPlayback`.
 */
import { getCapacitorPlugin, nativesPluginKennt } from "./native-voice.js";

interface SeitenTonPlugin {
  seitenTon(o: { an: boolean; frist: number }): Promise<unknown>;
}

/** So oft wird die Miete erneuert, solange etwas spielt. */
export const TAKT_MS = 5_000;
/** Frist der Erneuerung (Sekunden): reicht über einen verpassten Takt, auch wenn WebKit Timer drosselt. */
export const FRIST_ERNEUERUNG_S = 30;
/** Zuschlag auf die Stückdauer (Sekunden). */
const ZUSCHLAG_S = 3;
/** Frist bei unbekannter Stückdauer (Sekunden). */
export const FRIST_OHNE_DAUER_S = 20;
/** Länger als das nach dem letzten Stückbeginn gilt ein TTS-Zug nicht mehr als spielend. */
export const TTS_HOECHSTENS_MS = 5 * 60_000;
/** So lange läuft die Miete nach dem Ende weiter, bevor sie zurückgenommen wird. */
export const NACHLAUF_MS = 3_000;
/** Frist, die im Nachlauf gilt (Sekunden): deckt den Nachlauf samt Ausschlag ab. */
export const NACHLAUF_FRIST_S = 4;

/** Frist für ein Stück: Dauer + 3 s, ohne bekannte Dauer 20 s. */
export function fristFuerStueck(dauerS: number | undefined): number {
  if (typeof dauerS !== "number" || !Number.isFinite(dauerS) || dauerS <= 0) return FRIST_OHNE_DAUER_S;
  return Math.ceil(dauerS) + ZUSCHLAG_S;
}

export type Sende = (an: boolean, fristS: number) => void;

let idZaehler = 0;
/** Eine neue Kennung für eine Warteschlange. */
export function naechsteSeitenTonId(): string {
  idZaehler += 1;
  return `q${idZaehler}`;
}

const OHNE_ID = "?";

export class SeitenTonMelder {
  /** Je Warteschlange: wann begann das letzte Stück (oder wurde fortgesetzt)? */
  private readonly tts = new Map<string, number>();
  private readonly elemente = new Set<HTMLMediaElement>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private nachlauf: ReturnType<typeof setTimeout> | null = null;
  private gemeldet = false;

  constructor(
    private readonly sende: Sende,
    private readonly jetzt: () => number = () => Date.now(),
  ) {}

  /** Ein TTS-Stück beginnt. */
  ttsStart(dauerS?: number, queueId: string = OHNE_ID): void {
    this.tts.set(queueId, this.jetzt());
    this.abgleichen(fristFuerStueck(dauerS));
  }

  /** Die Wiedergabe wird nach einer Pause fortgesetzt — die Miete gilt wieder. */
  ttsWeiter(queueId: string = OHNE_ID): void {
    this.tts.set(queueId, this.jetzt());
    this.abgleichen();
  }

  /** Der Zug einer Warteschlange ist zu Ende oder abgebrochen. Ohne Kennung: alle. */
  ttsEnde(queueId?: string): void {
    if (queueId === undefined) this.tts.clear();
    else this.tts.delete(queueId);
    this.abgleichen();
  }

  elementStart(el: HTMLMediaElement): void {
    this.elemente.add(el);
    this.abgleichen();
  }

  elementEnde(el: HTMLMediaElement): void {
    this.elemente.delete(el);
    this.abgleichen();
  }

  /** Das Element beobachten: von `play` bis `pause`/`ended`/`error`/`abort`/`emptied`. */
  beobachteElement(el: HTMLMediaElement): void {
    el.addEventListener("play", () => this.elementStart(el));
    for (const ev of ["pause", "ended", "error", "abort", "emptied"]) {
      el.addEventListener(ev, () => this.elementEnde(el));
    }
  }

  /** Spielt gerade etwas? (Prüft den echten Zustand der Elemente.) */
  spielt(): boolean {
    for (const el of [...this.elemente]) {
      if (el.paused || el.ended) this.elemente.delete(el);
    }
    const jetzt = this.jetzt();
    for (const [id, seit] of [...this.tts]) {
      if (jetzt - seit >= TTS_HOECHSTENS_MS) this.tts.delete(id);
    }
    return this.tts.size > 0 || this.elemente.size > 0;
  }

  private abgleichen(frist: number = FRIST_ERNEUERUNG_S): void {
    if (this.spielt()) {
      this.nachlaufStop();
      this.sende(true, frist);
      this.gemeldet = true;
      if (!this.timer) this.timer = setInterval(() => this.abgleichen(), TAKT_MS);
      return;
    }
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (!this.gemeldet || this.nachlauf) return;
    // Nachlauf: die Miete läuft weiter; zurückgenommen wird erst nach Stille.
    this.sende(true, NACHLAUF_FRIST_S);
    this.nachlauf = setTimeout(() => {
      this.nachlauf = null;
      if (this.spielt()) return;
      this.gemeldet = false;
      this.sende(false, 0);
    }, NACHLAUF_MS);
  }

  private nachlaufStop(): void {
    if (!this.nachlauf) return;
    clearTimeout(this.nachlauf);
    this.nachlauf = null;
  }
}

/** An das Native — still, wenn die App die Methode nicht kennt. */
export function sendeAnNative(an: boolean, frist: number): void {
  try {
    if (!nativesPluginKennt("PawbotRecorder", "seitenTon")) return;
    const p = getCapacitorPlugin<SeitenTonPlugin>("PawbotRecorder");
    void p?.seitenTon({ an, frist }).catch(() => undefined);
  } catch {
    /* eine Meldung darf die Wiedergabe nie stören */
  }
}

/** Die eine Melderin der Seite. */
export const seitenTonMelder = new SeitenTonMelder(sendeAnNative);

/** Das Element beobachten (Kurzform für andere Player). */
export function beobachteElement(el: HTMLMediaElement): void {
  seitenTonMelder.beobachteElement(el);
}

let angeschlossen = false;

interface SeitenTonDetail {
  dauerS?: number;
  queueId?: string;
  /** 17.10.2026: die App spielt dieses Stück selbst (stimme-nativ.ts) — die Seite mietet dann keinen Ton. */
  nativ?: boolean;
}

/** Die TTS-Ereignisse anschließen (einmal). */
function schliesseSeitenTonAn(ziel: Window | undefined = typeof window === "undefined" ? undefined : window): void {
  if (!ziel || angeschlossen) return;
  angeschlossen = true;
  const detail = (ev: Event): SeitenTonDetail | null => (ev as CustomEvent<SeitenTonDetail | null>).detail;
  ziel.addEventListener("voice-v8:tts-audio-start", (ev) => {
    const d = detail(ev);
    if (d?.nativ === true) return;
    seitenTonMelder.ttsStart(d?.dauerS, d?.queueId);
  });
  ziel.addEventListener("voice-v8:tts-audio-end", (ev) => seitenTonMelder.ttsEnde(detail(ev)?.queueId));
  ziel.addEventListener("voice-v8:tts-audio-abbruch", (ev) => seitenTonMelder.ttsEnde(detail(ev)?.queueId));
}

schliesseSeitenTonAn();
